/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import Jsona from 'jsona';
import { isEmpty, cloneDeep } from 'lodash';
import Types from '../constants/actionTypes';
import * as API from '../utils/api';
import Store from '../store/store';
import { applicationError } from './notificationActions';
import { uploadAttachments } from './attachmentActions';
import { fetchAllTaskTypes } from './taskTypeAction';
import { openModal, closeModal } from './modalActions';
import { resultCardId } from '../utils/string';
import modalTypes from '../constants/modalTypes';
import deleteStrategies from '../constants/deleteStrategies';
import {
  updateSingleResult,
  addSingleResult,
  deleteSingleResult,
  updateMultipleResults
} from './commonDispatches';
import {
  extractImmediateDescendants,
  extractDeletedDescendants,
  extractNonDeletedChildren,
  unfinishedDescendantsAndAncestorsNotInTarget,
  extractChildren
} from '../utils/hierarchyHelpers';
import { setCurrentTeams, setCurrentProducts } from './defaultOptionsActions';
import { updateUserSpaceView } from './userActions';
import { WorkPackageStatuses } from '../constants/storyStatusTypes';
import {
  isWorkPackageInScope,
  isWorkPackageInStore
} from '../utils/workPackageHelpers';

import { newKanbanFilters } from '../utils/spaceHelpers';
import { userTourState } from '../utils/userHelpers';
import { clearKanbanFilter } from './savedViewActions';
import {
  getWorkPackage,
  getNonDeletedResults,
  getSelectedSpace
} from '../utils/reduxHelpers';
import { handleResultAuthorityChange } from '../utils/authorization';

const dataFormatter = new Jsona();
const cadenceId = () => Store.getState().cadenceReducer.cadence?.id;

function updateWorkPackageAttributesBasedOnStatus(workPackage, status) {
  if (status === WorkPackageStatuses.GETTING_READY) {
    return {
      ...workPackage,
      state: status,
      ready: false
    };
  }
  if (status === WorkPackageStatuses.READY) {
    return {
      ...workPackage,
      state: status,
      ready: true
    };
  }
  if (status === WorkPackageStatuses.IN_PROGRESS) {
    return {
      ...workPackage,
      state: status,
      completed: false
    };
  }
  if (status === WorkPackageStatuses.COMPLETED) {
    return {
      ...workPackage,
      state: status,
      completed: true,
      accepted: false
    };
  }
  if (status === WorkPackageStatuses.ACCEPTED) {
    return {
      ...workPackage,
      state: status,
      accepted: true,
      shippable: false
    };
  }
  if (status === WorkPackageStatuses.SHIPPABLE) {
    return {
      ...workPackage,
      state: status,
      accepted: true,
      shippable: true
    };
  }
  return {
    ...workPackage,
    state: status
  };
}

function handleDeleteStrategy(dispatch, strategy, result) {
  const cadence = cadenceId();
  switch (strategy) {
    case deleteStrategies.ADOPT:
      return dispatch({
        type: Types.REPLACE_WORK_PACKAGE_CHILDREN,
        oldParent: result,
        cadenceId: cadence
      });
    case deleteStrategies.ORPHAN:
      return dispatch({
        type: Types.REMOVE_CHILDREN_FROM_WORK_PACKAGE,
        workPackage: result,
        cadenceId: cadence
      });
    case deleteStrategies.DELETE:
      const { all } = Store.getState().workPackageReducer;
      const descendants =
        extractImmediateDescendants([result.id], all, result.level) || [];
      return dispatch({
        type: Types.DELETE_WORK_PACKAGE_CHILDREN,
        workPackage: result,
        descendants,
        cadenceId: cadence
      });
    default:
  }
}

export function updateWorkPackageFromBroadcast(
  result,
  ranksAndIds,
  deletionStrategy
) {
  return (dispatch) => {
    if (isWorkPackageInScope(result)) {
      if (isWorkPackageInStore(result)) {
        updateSingleResult(dispatch, result);
      } else {
        addSingleResult(dispatch, false, result, []);
      }
    } else {
      const { all } = Store.getState().workPackageReducer;
      if (all.some((wp) => wp.id === result.id)) {
        deleteSingleResult(dispatch, result);
      }
    }
    if (deletionStrategy) {
      handleDeleteStrategy(dispatch, deletionStrategy, result);
    }
  };
}

export function updateRanksFromBroadcast(ranksAndIds) {
  const cadence = cadenceId();
  return (dispatch) => {
    dispatch({
      type: Types.UPDATE_WORK_PACKAGE_RANKS,
      cadenceId: cadence,
      ranksAndIds
    });
  };
}

export function addWorkPackageFromBroadcast(result, ranksAndIds) {
  return (dispatch) => {
    if (isWorkPackageInScope(result)) {
      addSingleResult(dispatch, false, result, ranksAndIds);
    }
  };
}

export function deleteWorkPackageFromBroadcast(resultId) {
  const { workPackageReducer } = Store.getState();
  const { all } = workPackageReducer;
  return (dispatch) => {
    const result = all.find(
      (workPackage) => workPackage.id === Number(resultId)
    );
    deleteSingleResult(dispatch, result);
    const deletedDescendants =
      extractDeletedDescendants([result.id], all, result.level) || [];
    dispatch({
      type: Types.REMOVE_WORK_PACKAGES,
      workPackages: deletedDescendants,
      cadenceId: cadenceId()
    });
  };
}

export function updateWorkPackageList(data) {
  const cadence = cadenceId();
  return (dispatch) =>
    dispatch({
      type: Types.UPDATE_WORK_PACKAGE_LIST,
      workPackages: data,
      cadenceId: cadence
    });
}

export function fetchAllWorkPackages() {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_WORK_PACKAGE });
    return API.get('results')
      .then((response) => {
        const workPackages = dataFormatter.deserialize(response);
        const state = Store.getState();
        const { targets } = state.targetReducer;
        dispatch({
          workPackages,
          type: Types.RECEIVE_ALL_WORK_PACKAGES,
          cadenceId: cadenceId()
        });
        if (targets.length > 0) {
          dispatch({
            workPackages,
            type: Types.ADD_WORK_PACKAGES_TO_TARGETS
          });
        }
      })
      .catch((error) => {
        dispatch(applicationError(error));
        dispatch({ type: Types.REQUEST_WORK_PACKAGE_FAILED });
      });
  };
}

export function fetchTargetWorkPackages(id = 1) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_WORK_PACKAGE });
    if (!id) return dispatch(fetchAllWorkPackages());
    return API.get(`targets/${id}/work_packages`)
      .then((response) => {
        dispatch({
          type: Types.RECEIVE_TARGET_WORK_PACKAGES,
          workPackages: response.work_packages
        });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

const postWorkPackage = (
  payload = {},
  attachments = [],
  url = '',
  taskAttachments = {}
) => (dispatch) => {
  dispatch({ type: Types.SEND_WORK_PACKAGE });
  return API.post(`${url}work_packages`, payload)
    .then((response) => response.json())
    .then(async (responseData) => {
      const result = responseData.work_package
        ? dataFormatter.deserialize(responseData.work_package)
        : {};

      const ranksAndIds = responseData.ranks_and_ids
        ? JSON.parse(responseData.ranks_and_ids)
        : null;

      const tasks = result.tasks || [];
      try {
        if (attachments.length > 0)
          await dispatch(uploadAttachments(attachments, result.id));

        if (tasks.length > 0) {
          const taskAttachmentPromises = tasks.map((task, index) => {
            dispatch({ type: Types.RECEIVE_TASK, task });
            return dispatch(uploadAttachments(taskAttachments[index], task.id));
          });
          await Promise.all(taskAttachmentPromises);
        }
        dispatch(fetchAllTaskTypes());

        if (isWorkPackageInScope(result)) {
          addSingleResult(dispatch, true, result, ranksAndIds);
        }

        dispatch({ type: Types.END_UPLOAD_ATTACHMENT });

        return result;
      } catch (err) {
        if (isWorkPackageInScope(result)) {
          addSingleResult(dispatch, true, result, ranksAndIds);
        }

        tasks.forEach((task) => dispatch({ type: Types.RECEIVE_TASK, task }));
        dispatch({ type: Types.END_UPLOAD_ATTACHMENT });
        dispatch(
          applicationError('It was not possible to upload all the files')
        );
        return result;
      }
    })
    .catch((error) => {
      dispatch({ type: Types.END_UPLOAD_ATTACHMENT });
      dispatch(applicationError(error));
    });
};

export function createWorkPackage({
  data,
  cadence,
  type,
  contribution = {},
  tasks,
  criteria,
  attachments = [],
  state,
  children = [],
  taskAttachments = {},
  modalId,
  childOrgLevel
}) {
  const dataCopy = cloneDeep(data);
  if (data.work_package && !data.work_package.level) {
    dataCopy.work_package.level = data.work_package.level || 1;
  }
  const payload = {
    ...dataCopy,
    result_type: type || {},
    cadence_id: cadence,
    contribution,
    tasks,
    criteria,
    state,
    children
  };

  const url = cadence && cadence > 0 ? `cadences/${cadence}/` : '';
  return async (dispatch) => {
    const response = await dispatch(
      postWorkPackage(payload, attachments, url, taskAttachments)
    );
    const childrenPromises = children.map((child) => {
      const workPackage = child;
      workPackage.parent_id = response.id;
      workPackage.level = response.level - 1;
      return dispatch(
        postWorkPackage(
          { work_package: workPackage, result_type: childOrgLevel },
          [],
          url
        )
      );
    });
    await Promise.all(childrenPromises);
    dispatch({ type: Types.CANCEL_SEND_WORK_PACKAGE });
    if (modalId) dispatch({ type: Types.CLOSE_MODAL, id: modalId });
  };
}

export function cloneWorkPackage(workPackageId) {
  const url = cadenceId() ? `cadences/${cadenceId()}/` : '';

  return (dispatch) => {
    dispatch({ type: Types.SEND_WORK_PACKAGE });
    return API.put(`${url}work_packages/${workPackageId}/clone`)
      .then((response) => response.json())
      .then((responseData) => {
        const result = responseData.work_package
          ? dataFormatter.deserialize(responseData.work_package)
          : {};

        const ranksAndIds = responseData.ranks_and_ids
          ? JSON.parse(responseData.ranks_and_ids)
          : [];
        addSingleResult(dispatch, false, result, ranksAndIds);
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function removeSpace(spaceId, type) {
  return (dispatch) => {
    let newSavedSpace = getSelectedSpace();
    const { id: selectedId, type: selectedType } = newSavedSpace;

    if (type === selectedType && spaceId === selectedId) {
      newSavedSpace = { id: 'unassigned', initialSpace: false };
      window.localStorage.setItem(
        'selectedSpace',
        JSON.stringify(newSavedSpace)
      );
      dispatch({ type: Types.CLEAR_ACTIVE_VIEW });
      dispatch({ type: Types.CLEAR_FILTERS_ON_SPACE_CHANGE });
      dispatch(clearKanbanFilter());
      dispatch({
        type: Types.SET_SELECTED_SPACE,
        selectedSpace: newSavedSpace
      });
      dispatch(setCurrentTeams([]));
      dispatch(setCurrentProducts([]));
    }

    const {
      planwallStates: planwall_defaults,
      savedViews: saved_views,
      previousStates: previous_views,
      kanbanWallState: kanban_state,
      savedKanbanFilters
    } = Store.getState().savedViewReducer;

    const newSavedViews = saved_views.filter((sv) => sv.space_id !== spaceId);

    dispatch(
      updateUserSpaceView({
        user: {
          saved_space_view: {
            saved_space: newSavedSpace,
            planwall_defaults,
            saved_views: newSavedViews,
            kanban_state,
            savedKanbanFilters,
            tourState: userTourState()
          }
        }
      })
    );
  };
}

function setCurrentProductsAndTeams(strippedSpace, dispatch) {
  // identify what kind of space is selected
  // then grab the appropriate team/product from the respective reducer based on the space
  // store that as the selected space
  let selectedSpace = strippedSpace;

  if (strippedSpace.type === 'product') {
    const { products } = Store.getState().productReducer;
    selectedSpace = products.find((product) => product.id === strippedSpace.id);
    const teamIds = selectedSpace.teams.map((team) => team.id);
    dispatch(setCurrentTeams(teamIds, true));

    dispatch(setCurrentProducts([selectedSpace.id], true));
  } else if (strippedSpace.type === 'team') {
    const { teams } = Store.getState().teamReducer;
    selectedSpace = teams.find((team) => team.id === strippedSpace.id);

    dispatch(setCurrentTeams(selectedSpace ? [selectedSpace.id] : [], true));

    const productIds = selectedSpace
      ? selectedSpace.products.map((prod) => prod.id)
      : [];
    dispatch(setCurrentProducts(productIds, true));
  }
  dispatch({ type: Types.SET_SELECTED_SPACE, selectedSpace });

  if (strippedSpace.id === 'unassigned') {
    // happens for initial new user authentication
    const unassignedObj = [];
    dispatch(setCurrentProducts(unassignedObj, true));
    dispatch(setCurrentTeams(unassignedObj, true, null));
  }
}

export function setSelectedSpace(space, type) {
  return (dispatch) => {
    const { id } = space;
    const strippedSpace = {
      id,
      type
    };

    const previousSpace = localStorage.selectedSpace
      ? JSON.parse(localStorage.selectedSpace)
      : {};

    window.localStorage.setItem('selectedSpace', JSON.stringify(strippedSpace));

    setCurrentProductsAndTeams(strippedSpace, dispatch);

    const {
      planwallStates: planwall_defaults,
      savedViews: saved_views,
      previousStates: previous_views,
      kanbanWallState: kanban_state,
      savedKanbanFilters
    } = Store.getState().savedViewReducer;

    dispatch({ type: Types.CLEAR_ACTIVE_VIEW });
    dispatch({ type: Types.CLEAR_FILTERS_ON_SPACE_CHANGE });

    const newSavedKanbanFilters = newKanbanFilters(
      previousSpace,
      savedKanbanFilters,
      kanban_state
    );

    dispatch(clearKanbanFilter());

    dispatch(
      updateUserSpaceView({
        user: {
          saved_space_view: {
            saved_space: strippedSpace,
            planwall_defaults,
            saved_views,
            kanban_state,
            saved_kanban_filters: newSavedKanbanFilters,
            tourState: userTourState()
          }
        }
      })
    );

    dispatch({ type: Types.SET_SELECTED_SPACE, selectedSpace: space });
  };
}

export function selectedSpaceOnAuth(strippedSpace) {
  return (dispatch) => {
    window.localStorage.setItem('selectedSpace', JSON.stringify(strippedSpace));

    setCurrentProductsAndTeams(strippedSpace, dispatch);

    if (localStorage.authenticated) return;
    window.localStorage.setItem('authenticated', true);
  };
}

export function updateWorkPackageWithParent(response, dispatch) {
  let { work_package, parent } = response;

  work_package = dataFormatter.deserialize(work_package);
  parent = dataFormatter.deserialize(parent);

  updateSingleResult(dispatch, work_package);
  updateSingleResult(dispatch, parent);

  const tasks = work_package.tasks || [];

  dispatch({
    type: Types.RECEIVE_WORK_PACKAGE_TASKS,
    tasks
  });

  const childDescendants = work_package.descendants || [];
  const parentDescendants = parent.descendants || [];

  dispatch({
    type: Types.UPDATE_WORK_PACKAGE_CHILDREN,
    workPackages: childDescendants,
    cadenceId: cadenceId()
  });

  dispatch({
    type: Types.UPDATE_WORK_PACKAGE_CHILDREN,
    workPackages: parentDescendants,
    cadenceId: cadenceId()
  });
}

const authorizeResultOwners = (resultId, data) => {
  let result = getWorkPackage(resultId);
  const changedValues = data.work_package;
  const changedEntries = Object.entries(changedValues)[0];

  result = handleResultAuthorityChange(
    result,
    changedEntries[0],
    changedEntries[1]
  );
  if (!result.owner_id) data.work_package.owner_id = null;
  if (!result.product_owner_id) data.work_package.product_owner_id = null;
};

export function updateWorkPackage(id, data) {
  const url = cadenceId() ? `cadences/${cadenceId()}/` : '';
  const cadence = cadenceId();
  const changedAttributes = Object.keys(data?.work_package || data);

  if (
    changedAttributes.includes('team_id') ||
    changedAttributes.includes('product_id')
  ) {
    authorizeResultOwners(id, data);
  }
  return (dispatch) => {
    if (changedAttributes.includes('team_id')) {
      dispatch({ type: Types.CLEAR_RESULT_CRITERIA, id });
    }
    dispatch({ type: Types.SEND_WORK_PACKAGE });
    return API.put(`${url}work_packages/${id}`, data)
      .then((response) => response.json())
      .then((responseData) => {
        if (responseData.parent)
          return updateWorkPackageWithParent(responseData, dispatch);

        const result = responseData
          ? dataFormatter.deserialize(responseData)
          : {};

        if (!isWorkPackageInScope(result)) {
          return deleteSingleResult(dispatch, result);
        }

        const tasks = result.tasks || [];
        const descendants = result.descendants || [];

        updateSingleResult(dispatch, result);

        dispatch({
          type: Types.UPDATE_WORK_PACKAGE_CHILDREN,
          workPackages: descendants,
          cadenceId: cadence
        });

        dispatch({
          type: Types.RECEIVE_WORK_PACKAGE_TASKS,
          tasks
        });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

const getLastWorkPackageRank = (workPackage) => {
  const { workPackageReducer } = Store.getState();

  switch (workPackage.level) {
    case '1':
      return workPackageReducer.stories[workPackageReducer.stories.length - 1]
        .rank;
    case '2':
      return workPackageReducer.features[workPackageReducer.features.length - 1]
        .rank;
    case '3':
      return workPackageReducer.capabilities[
        workPackageReducer.capabilities.length - 1
      ].rank;
    default:
      return workPackageReducer.stories[workPackageReducer.stories.length - 1]
        .rank;
  }
};

export function updateWorkPackageRank(
  workPackage,
  data,
  altCadence,
  queueLength
) {
  const cadence = altCadence || cadenceId();
  const url = cadence ? `cadences/${cadence}/` : '';

  const { position, relativeTo } = data;
  const rankData = relativeTo ? { ...data, relative_to: relativeTo.id } : data;
  rankData.state = workPackage.state;
  if (queueLength && queueLength > 0) {
    rankData.no_broadcasting = true;
  }

  let newRank;

  if (position === 'after') newRank = relativeTo.rank + 0.5;
  else if (position === 'before') newRank = relativeTo.rank - 0.5;
  else if (position === 'start') newRank = 0.5;
  else if (position === 'end')
    newRank = getLastWorkPackageRank(workPackage) + 0.5;

  const rankedWorkPackage = {
    ...JSON.parse(JSON.stringify(workPackage)),
    rank: newRank
  };
  return (dispatch) => {
    dispatch({ type: Types.SEND_WORK_PACKAGE });
    dispatch({
      type: Types.UPDATE_WORK_PACKAGE_RANK,
      workPackage: rankedWorkPackage,
      cadenceId: cadence
    });

    dispatch({
      type: Types.UPDATE_WORK_PACKAGE_STATUS,
      state: rankedWorkPackage.state,
      workPackageId: rankedWorkPackage.id,
      cadenceId: cadence
    });
    return API.put(`${url}work_packages/${workPackage.id}/rank`, rankData)
      .then((response) => response.json())
      .then((responseData) => {
        const result = responseData.work_package
          ? dataFormatter.deserialize(responseData.work_package)
          : {};

        const ranksAndIds = responseData.ranks_and_ids
          ? JSON.parse(responseData.ranks_and_ids)
          : [];

        const tasks = result.tasks || [];

        dispatch({
          type: Types.UPDATE_WORK_PACKAGE_RANKS,
          cadenceId: cadence,
          ranksAndIds
        });
        dispatch({
          type: Types.RECEIVE_WORK_PACKAGE_TASKS,
          tasks
        });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function updateWorkPackageStatus(workPackageId, status, oldStatus) {
  const cadence = cadenceId();
  const url = cadence ? `cadences/${cadence}/` : '';
  const { taskReducer, workPackageReducer } = Store.getState();
  let workPackage = workPackageReducer.all.find((w) => w.id === workPackageId);
  workPackage = updateWorkPackageAttributesBasedOnStatus(workPackage, status);
  const { workPackagedTasks } = taskReducer;
  const wpTasks =
    workPackagedTasks && workPackagedTasks[workPackage.id]
      ? workPackagedTasks[workPackage.id]
      : [];
  const updatedTasks = wpTasks.map((task) => {
    const newTask = task;
    newTask.completed = workPackage.completed
      ? workPackage.completed
      : task.completed;
    newTask.started = workPackage.started ? workPackage.started : task.started;
    return newTask;
  });

  return (dispatch) => {
    dispatch({ type: Types.SEND_WORK_PACKAGE });

    updateSingleResult(dispatch, workPackage);
    dispatch({
      type: Types.RECEIVE_WORK_PACKAGE_TASKS,
      tasks: updatedTasks
    });
    return API.put(`${url}work_packages/${workPackageId}/change-status`, {
      state: status
    })
      .then((response) => response.json())
      .then((responseData) => {
        const result = responseData
          ? dataFormatter.deserialize(responseData)
          : {};
        const tasks = result.tasks || [];

        updateSingleResult(dispatch, result);
        dispatch({
          type: Types.RECEIVE_WORK_PACKAGE_TASKS,
          tasks
        });
      })
      .catch((error) => {
        dispatch({
          type: Types.UPDATE_WORK_PACKAGE_STATUS,
          workPackageId,
          state: oldStatus,
          cadenceId: cadence
        });
        return dispatch(applicationError(error));
      });
  };
}

// Update status but also other attributes like completed, accepted, ...
// Then all workPackage has to be send to store and not only status attribute
export function updateAllAttributesWorkPackageStatus(
  oldWorkPackage,
  workPackage,
  status
) {
  const cadence = cadenceId();
  const { taskReducer } = Store.getState();
  const { workPackagedTasks } = taskReducer;
  const wpTasks =
    workPackagedTasks && workPackagedTasks[workPackage.id]
      ? workPackagedTasks[workPackage.id]
      : [];
  const updatedTasks = wpTasks.map((task) => {
    const newTask = task;
    newTask.completed = workPackage.completed
      ? workPackage.completed
      : task.completed;
    newTask.started = workPackage.started ? workPackage.started : task.started;
    return newTask;
  });
  const url = cadence ? `cadences/${cadence}/` : '';

  return (dispatch) => {
    dispatch({ type: Types.SEND_WORK_PACKAGE });

    updateSingleResult(dispatch, workPackage);
    dispatch({
      type: Types.RECEIVE_WORK_PACKAGE_TASKS,
      tasks: updatedTasks
    });
    return API.put(`${url}work_packages/${workPackage.id}/change-status`, {
      state: status
    })
      .then((response) => response.json())
      .then((responseData) => {
        const result = responseData
          ? dataFormatter.deserialize(responseData)
          : {};
        const tasks = result.tasks || [];

        updateSingleResult(dispatch, result);
        dispatch({
          type: Types.RECEIVE_WORK_PACKAGE_TASKS,
          tasks
        });
      })
      .catch((error) => {
        updateSingleResult(dispatch, oldWorkPackage);
        return dispatch(applicationError(error));
      });
  };
}

// onlyFromSource causes only the descendants of the work package that are in the source target to move
export function addWorkPackageToTarget(
  workPackageId,
  targetId,
  onlyFromSource = false
) {
  const { workPackageReducer } = Store.getState();
  const { all } = workPackageReducer;
  const currentWorkPackage = all.find((wp) => wp.id === workPackageId);
  const oldTargetId = currentWorkPackage.target_id;

  const payload = { target_id: targetId, only_from_source: onlyFromSource };

  return (dispatch) => {
    dispatch({ type: Types.SEND_WORK_PACKAGE });
    return API.put(`work_packages/${workPackageId}/add-target`, payload)
      .then((response) => response.json())
      .then((responseData) => {
        const results = dataFormatter.deserialize(responseData);
        if (results.length > 1) {
          updateMultipleResults(dispatch, results);

          dispatch({
            type: Types.ADD_WORK_PACKAGES_TO_TARGET,
            results,
            targetId
          });
          if (oldTargetId) {
            dispatch({
              type: Types.REMOVE_WORK_PACKAGES_FROM_TARGET,
              targetId: oldTargetId,
              results
            });
          }
        } else {
          const result = results[0];

          updateSingleResult(dispatch, result);

          dispatch({
            type: Types.ADD_WORK_PACKAGE_TO_TARGET,
            targetId: result.target_id,
            workPackage: result
          });
          if (oldTargetId) {
            dispatch({
              type: Types.REMOVE_WORK_PACKAGE_FROM_TARGET,
              targetId: oldTargetId,
              workPackage: result
            });
          }
        }
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function addWorkPackagesToTarget(
  workPackageId,
  targetId,
  descendantAncestorIds
) {
  const { workPackageReducer } = Store.getState();
  const { all } = workPackageReducer;
  const currentWorkPackage = all.find((wp) => wp.id === workPackageId);
  const oldTargetId = currentWorkPackage.target_id;

  const payload = {
    target_id: targetId,
    descendant_ancestor_ids: descendantAncestorIds
  };

  return (dispatch) => {
    dispatch({ type: Types.SEND_WORK_PACKAGE });
    return API.put(`work_packages/${workPackageId}/add-target`, payload)
      .then((response) => response.json())
      .then((responseData) => {
        const results = dataFormatter.deserialize(responseData);
        if (results.length > 1) {
          updateMultipleResults(dispatch, results);

          dispatch({
            type: Types.ADD_WORK_PACKAGES_TO_TARGET,
            results,
            targetId
          });

          if (oldTargetId) {
            dispatch({
              type: Types.REMOVE_WORK_PACKAGES_FROM_TARGET,
              targetId: oldTargetId,
              results
            });
          }
        } else {
          const result = results[0];
          updateSingleResult(dispatch, result);
          dispatch({
            type: Types.ADD_WORK_PACKAGE_TO_TARGET,
            targetId: result.target_id,
            workPackage: result
          });
          if (oldTargetId) {
            dispatch({
              type: Types.REMOVE_WORK_PACKAGE_FROM_TARGET,
              targetId: oldTargetId,
              workPackage: result
            });
          }
        }
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function addWorkPackageToTargetWithUnfinishedDescendantsAndAncestors(
  workPackageId,
  targetId
) {
  const { workPackageReducer, targetReducer } = Store.getState();
  const { all } = workPackageReducer;

  const workPackage = all.find((w) => w.id === workPackageId);
  const target = targetReducer.targets.find((t) => t.id === targetId);
  const descendantsAndAncestors = unfinishedDescendantsAndAncestorsNotInTarget(
    workPackage,
    all,
    target
  );

  const idsToAdd = descendantsAndAncestors.map((wp) => wp.id);
  idsToAdd.push(workPackage.id);
  return addWorkPackagesToTarget(workPackage.id, target.id, idsToAdd);
}

export function removeWorkPackageFromTarget(workPackageId, targetId) {
  const payload = { target_id: targetId };
  return (dispatch) => {
    dispatch({ type: Types.SEND_WORK_PACKAGE });
    return API.put(`work_packages/${workPackageId}/remove-target`, payload)
      .then((response) => response.json())
      .then((responseData) => {
        const data = dataFormatter.deserialize(responseData);
        if (data.length > 1) {
          updateMultipleResults(dispatch, data);
        } else {
          updateSingleResult(dispatch, data[0]);
        }
        dispatch({
          type: Types.REMOVE_WORK_PACKAGES_FROM_TARGET,
          targetId,
          results: data
        });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function removeWorkPackageFromCadence(workPackageId, oldCadenceId) {
  const payload = { work_package: { cadence_id: oldCadenceId } };
  const { cadenceReducer } = Store.getState();
  const { cadences } = cadenceReducer;
  return (dispatch) => {
    dispatch({ type: Types.SEND_WORK_PACKAGE });
    return API.put(
      `cadences/${oldCadenceId}/work_packages/${workPackageId}/remove-cadence`,
      payload
    )
      .then((response) => response.json())
      .then((responseData) => {
        const result = responseData
          ? dataFormatter.deserialize(responseData)
          : {};
        updateSingleResult(dispatch, result);
        if (!isEmpty(result.tasks)) {
          dispatch({
            type: Types.RECEIVE_WORK_PACKAGE_TASKS,
            tasks: result.tasks
          });
        }
        if (result.cadence_ids && result.cadence_ids.length > 0) {
          const newCadence = cadences.find(
            (c) => c.id === result.cadence_ids[0]
          );
          if (newCadence && newCadence.work_package_ids) {
            newCadence.work_package_ids.push(result.id);
            dispatch({
              type: Types.UPDATE_CADENCE,
              cadence: newCadence
            });
          }
        }
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function addWorkPackageToCadence(workPackageId, newCadenceId) {
  const cadence = newCadenceId || cadenceId();
  const { workPackageReducer, cadenceReducer } = Store.getState();
  const { all } = workPackageReducer;
  const { cadences } = cadenceReducer;
  const oldWorkPackage = all.find((wp) => wp.id === workPackageId);
  const oldCadence = cadences.find(
    (c) =>
      oldWorkPackage.cadence_ids.length > 0 &&
      c.id === oldWorkPackage.cadence_ids[0]
  );
  const newCadence = cadences.find((c) => c.id === cadence);
  const url = cadence ? `cadences/${cadence}/` : '';
  return (dispatch) => {
    dispatch({ type: Types.SEND_WORK_PACKAGE });
    return API.put(`${url}work_packages/${workPackageId}/add-cadence`)
      .then((response) => response.json())
      .then((responseData) => {
        const result = responseData
          ? dataFormatter.deserialize(responseData)
          : {};

        updateSingleResult(dispatch, result);
        if (!isEmpty(result.tasks)) {
          dispatch({
            type: Types.RECEIVE_WORK_PACKAGE_TASKS,
            tasks: result.tasks
          });
        }
        if (oldCadence && oldCadence.work_package_ids) {
          const updatedWorkPackageIds = oldCadence.work_package_ids.filter(
            (id) => id !== result.id
          );
          oldCadence.work_package_ids = updatedWorkPackageIds;
          dispatch({
            type: Types.UPDATE_CADENCE,
            cadence: oldCadence
          });
        }
        if (newCadence && newCadence.work_package_ids) {
          newCadence.work_package_ids.push(result.id);
          dispatch({
            type: Types.UPDATE_CADENCE,
            cadence: newCadence
          });
        }
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function moveWorkPackageinCadence(workPackageId, newCadenceId) {
  const payload = { work_package: { cadence_id: newCadenceId } };
  const currentCadenceId = cadenceId();
  const url = currentCadenceId ? `cadences/${currentCadenceId}/` : '';
  return (dispatch) => {
    if (currentCadenceId)
      dispatch(removeWorkPackageFromCadence(workPackageId, currentCadenceId));
    return API.put(
      `${url}work_packages/${workPackageId}/add-cadence`,
      payload
    ).catch((error) => dispatch(applicationError(error)));
  };
}

export function blockWorkPackage(workPackageId) {
  return (dispatch) => {
    dispatch({ type: Types.SEND_WORK_PACKAGE });
    return API.put(`workPackages/${workPackageId}/block`)
      .then((response) => response.json())
      .then((responseData) => {
        const result = responseData
          ? dataFormatter.deserialize(responseData)
          : {};

        updateSingleResult(dispatch, result);
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function deleteWorkPackage(workPackageId, strategy) {
  return (dispatch) => {
    dispatch({ type: Types.SEND_WORK_PACKAGE });
    return API.del(`work_packages/${workPackageId}`, { strategy })
      .then((response) => response.json())
      .then((responseData) => {
        const result = dataFormatter.deserialize(responseData);
        if (result['destroyed?']) {
          deleteSingleResult(dispatch, result);
          deleteSingleResult(dispatch, result);
          const { workPackageReducer } = Store.getState();
          const { all } = workPackageReducer;
          const deletedDescendants =
            extractDeletedDescendants([result.id], all, result.level) || [];

          dispatch({
            type: Types.REMOVE_WORK_PACKAGES,
            workPackages: deletedDescendants,
            cadenceId: cadenceId()
          });
          return Promise.resolve(result);
        }
        updateSingleResult(dispatch, result);

        handleDeleteStrategy(dispatch, strategy, result);
        return Promise.resolve(result);
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function willDeleteWorkPackage(workPackageId) {
  const { workPackageReducer } = Store.getState();
  const { all } = workPackageReducer;
  const wholesomeResults = getNonDeletedResults();

  const workPackage = all.find((wp) => wp.id === workPackageId);
  const children = extractNonDeletedChildren(workPackage.id, all);
  const descendants = extractImmediateDescendants(
    [workPackage.id],
    wholesomeResults,
    workPackage.level
  );

  const descendantsLength = descendants.length - children.length;
  const message = `${resultCardId(workPackage)} has ${children.length} ${
    children.length === 1 ? 'child' : 'children'
  } and ${descendantsLength} additional
  ${
    descendantsLength === 1 ? 'descendant' : 'descendants'
  }. What do you want to do with them?`;
  const actions = {};

  return (dispatch) => {
    if (children.length > 0 && !workPackage.deleted_at) {
      if (workPackage.parent_id) {
        const parent = all.find((wp) => wp.id === workPackage.parent_id);
        actions.adopt = {
          name: `Assign to ${resultCardId(parent)}`,
          function: () =>
            dispatch(deleteWorkPackage(workPackage.id, deleteStrategies.ADOPT))
        };
      }
      actions.orphan = {
        name: 'Orphan',
        function: () =>
          dispatch(deleteWorkPackage(workPackage.id, deleteStrategies.ORPHAN))
      };
      actions.delete = {
        name: `Delete${descendants.length === 1 ? ' ' : ' all of '}the ${
          descendants.length
        } ${
          descendants.length === 1 ? 'descendant' : 'descendants'
        } of ${resultCardId(workPackage)}`,
        function: () =>
          dispatch(deleteWorkPackage(workPackage.id, deleteStrategies.DELETE))
      };

      dispatch(
        openModal(
          modalTypes.message,
          {
            message,
            actions,
            closeButtonTitle: 'Cancel'
          },
          'confirm'
        )
      );
    } else {
      dispatch(deleteWorkPackage(workPackage.id, deleteStrategies.DELETE));
    }
  };
}

export function restoreWorkPackage(workPackageId) {
  return (dispatch) => {
    const cadence = cadenceId();
    dispatch({ type: Types.SEND_WORK_PACKAGE });
    return API.put(`work_packages/${workPackageId}/restore`)
      .then((response) => response.json())
      .then((responseData) => {
        const result = dataFormatter.deserialize(responseData);
        const descendants = result.descendants || [];

        updateSingleResult(dispatch, result);
        dispatch({
          type: Types.UPDATE_WORK_PACKAGE_CHILDREN,
          workPackages: descendants,
          cadenceId: cadence
        });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function sortList(order, field) {
  return (dispatch) => {
    dispatch({ type: Types.SORT_WORK_PACKAGES, order, field });
  };
}

export function updateWorkPackageCapability(
  workPackageId,
  capabilityId,
  operation
) {
  const payload = { capability_id: capabilityId, operation };
  const cadence = cadenceId();
  return (dispatch) => {
    dispatch({ type: Types.SEND_WORK_PACKAGE });
    return API.put(`work_packages/${workPackageId}/update-capability`, payload)
      .then((response) => response.json())
      .then((responseData) => {
        const result = responseData
          ? dataFormatter.deserialize(responseData)
          : {};
        const tasks = result.tasks || [];

        updateSingleResult(dispatch, result);
        dispatch({
          type: Types.RECEIVE_WORK_PACKAGE_TASKS,
          tasks
        });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function updateWorkPackageLevel(
  workPackage,
  level,
  organizationLevelId
) {
  const payload = {
    level,
    organization_level_id: organizationLevelId,
    type_attributes: workPackage.result_type.type_attributes
  };
  if (level > 1 && workPackage.level === 1) {
    if (
      !window.confirm(
        'This item will be removed from all of its sprints, and its tasks will be deleted. Continue?'
      )
    ) {
      return false;
    }
  }

  return async (dispatch) => {
    if (level < workPackage.level && workPackage.has_children) {
      const { all } = Store.getState().workPackageReducer;
      const children = extractChildren(workPackage.id, all);

      const childrenToOrphan = children.filter(
        (child) => child.level >= level && child.level < workPackage.level
      );

      if (childrenToOrphan.length > 0) {
        // it might be 0 if all the children are skip-level
        return dispatch(
          openModal(
            modalTypes.orphanChildren,
            {
              children: childrenToOrphan,
              payload,
              workPackage,
              handleModalClose: () =>
                dispatch(closeModal(null, `ORPHAN_CHILDREN_${workPackage.id}`))
            },
            `ORPHAN_CHILDREN_${workPackage.id}`
          )
        );
      }
    }
    await dispatch(updateWorkPackageLevelApiCall(workPackage, payload));
  };
}

export function updateWorkPackageLevelApiCall(workPackage, payload) {
  return async (dispatch) => {
    dispatch({ type: Types.SEND_WORK_PACKAGE });
    return API.put(`work_packages/${workPackage.id}/update-level`, payload)
      .then((response) => response.json())
      .then((responseData) => {
        const result = responseData
          ? dataFormatter.deserialize(responseData)
          : {};

        updateSingleResult(dispatch, result);
        if (result.level === 1) {
          dispatch({
            type: Types.REMOVE_CHILDREN_FROM_WORK_PACKAGE,
            workPackage: result
          });
        }
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function orphanChildren(workPackageIds) {
  return (dispatch) => {
    dispatch({ type: Types.SEND_WORK_PACKAGE });
    return API.put('work_packages/orphan_children', {
      work_package_ids: workPackageIds
    })
      .then((response) => response.json())
      .then((responseData) => {
        const results = dataFormatter.deserialize(responseData);
        updateMultipleResults(dispatch, results);
      });
  };
}

export function updateWorkPackageOwner(workPackageId, ownerId) {
  const payload = { owner_id: ownerId };
  return (dispatch) => {
    dispatch({ type: Types.SEND_WORK_PACKAGE });
    return API.put(`work_packages/${workPackageId}/edit-owner`, payload)
      .then((response) => response.json())
      .then((responseData) => {
        const result = responseData
          ? dataFormatter.deserialize(responseData)
          : {};
        updateSingleResult(dispatch, result);
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function addWrapParent(parentId) {
  return (dispatch) => {
    dispatch({
      type: Types.ADD_WRAP_PARENT,
      parentId
    });
  };
}

export function removeWrapParent(parentId) {
  return (dispatch) => {
    dispatch({
      type: Types.REMOVE_WRAP_PARENT,
      parentId
    });
  };
}

export function resetWrapParent() {
  return (dispatch) => {
    dispatch({
      type: Types.RESET_WRAP_PARENT
    });
  };
}

export function getBurndownData(workPackageId) {
  return (dispatch) => {
    dispatch({
      workPackageId,
      type: Types.REQUEST_WORK_PACKAGE_GRAPHS,
      graph: 'burndown'
    });
    return API.get(`work_packages/${workPackageId}/burndown`)
      .then((response) => {
        dispatch({
          workPackageId,
          type: Types.ADD_WORK_PACKAGE_METRICS_BURNDOWN,
          burndown: response
        });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function getLeanSeries(
  workPackageId,
  resultType = { risk: '', value: '', size: '' }
) {
  return (dispatch) => {
    dispatch({
      workPackageId,
      type: Types.REQUEST_WORK_PACKAGE_GRAPHS,
      graph: 'lean'
    });
    return API.get(
      `work_packages/${workPackageId}/lean-metrics`,
      `risk=${resultType.risk}&size=${resultType.size}&value=${resultType.value}`,
      false
    )
      .then((response) =>
        dispatch({
          workPackageId,
          type: Types.ADD_WORK_PACKAGE_METRICS_LEAN,
          lean: response
        })
      )
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function getWorkDensitySeries(workPackageId) {
  return (dispatch) => {
    dispatch({
      workPackageId,
      type: Types.REQUEST_WORK_PACKAGE_GRAPHS,
      graph: 'idle'
    });
    return API.get(`work_packages/${workPackageId}/work-density`)
      .then((response) =>
        dispatch({
          workPackageId,
          type: Types.ADD_WORK_PACKAGE_METRICS_IDLE,
          idle: response
        })
      )
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function getCumulativeFlowSeries(workPackageId) {
  return (dispatch) => {
    dispatch({
      workPackageId,
      type: Types.REQUEST_WORK_PACKAGE_GRAPHS,
      graph: 'cumulative'
    });
    return API.get(`work_packages/${workPackageId}/cumulative-flow-series`)
      .then((response) =>
        dispatch({
          workPackageId,
          type: Types.ADD_WORK_PACKAGE_METRICS_CUMULATIVE,
          cumulative: response.work_packages
        })
      )
      .catch((error) => dispatch(applicationError(error)));
  };
}

const resultsByLevel = (level) => {
  const { features, capabilities, epics } = Store.getState().workPackageReducer;
  switch (level) {
    case 2:
      return features;
    case 3:
      return capabilities;
    case 4:
      return epics;
    default:
      return [];
  }
};

// Retrieve the children progress bar data for a single work package
export function getChildrenProgress(children) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_WORK_PACKAGE });
    const payload = {
      work_package_ids: children.map((c) => c.id)
    };
    return API.post('work_packages/children_progress', payload)
      .then((response) => response.json())
      .then((data) => {
        dispatch({
          type: Types.RECEIVE_CHILDREN_PROGRESS,
          childrenProgress: data.children_progress
        });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

// Retrieve the children progress bar data all loaded work packages of the supplied level
export function getChildrenProgressByLevel(level) {
  return (dispatch) => {
    dispatch({ type: Types.REQUEST_WORK_PACKAGE });
    const results = resultsByLevel(level);
    const resultIds = results.map((r) => r.id);
    const payload = {
      work_package_ids: resultIds
    };
    return API.post('work_packages/children_progress', payload)
      .then((response) => response.json())
      .then((data) => {
        dispatch({
          type: Types.RECEIVE_CHILDREN_PROGRESS,
          childrenProgress: data.children_progress
        });
      })
      .catch((error) => dispatch(applicationError(error)));
  };
}

export function fetchGanttData(wpid, isStoryCount) {
  return (dispatch) =>
    API.post(`work_packages/${wpid}/gantt_series`, {
      story_count: isStoryCount
    })
      .then((response) => response.json())
      .then((data) => {
        dispatch({
          type: Types.ADD_WORK_PACKAGE_METRICS_GANTT,
          gantt: data,
          workPackageId: wpid
        });
      });
}
